import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  CircleNotch,
  Check,
  X,
} from '@phosphor-icons/react'
import { orgApi } from '@/api/organizations'
import type { CreateOrgRequest } from '@/api/organizations'
import { generateSlug } from '@/lib/slug'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const INITIAL_STATE = {
  name: '',
  slug: '',
  vertical_slug: '',
  admin_email: '',
  admin_name: '',
  subscription_tier: 'free',
  max_users: 50,
}

export function OrganizationCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Form state
  const [name, setName] = useState(INITIAL_STATE.name)
  const [slug, setSlug] = useState(INITIAL_STATE.slug)
  const [verticalSlug, setVerticalSlug] = useState(INITIAL_STATE.vertical_slug)
  const [adminEmail, setAdminEmail] = useState(INITIAL_STATE.admin_email)
  const [adminName, setAdminName] = useState(INITIAL_STATE.admin_name)
  const [subscriptionTier, setSubscriptionTier] = useState(INITIAL_STATE.subscription_tier)
  const [maxUsers, setMaxUsers] = useState(INITIAL_STATE.max_users)

  // Slug tracking
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [debouncedSlug, setDebouncedSlug] = useState('')
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Dirty tracking -- any field differs from initial
  const isDirty = useMemo(() => {
    return (
      name !== INITIAL_STATE.name ||
      slug !== INITIAL_STATE.slug ||
      verticalSlug !== INITIAL_STATE.vertical_slug ||
      adminEmail !== INITIAL_STATE.admin_email ||
      adminName !== INITIAL_STATE.admin_name ||
      subscriptionTier !== INITIAL_STATE.subscription_tier ||
      maxUsers !== INITIAL_STATE.max_users
    )
  }, [name, slug, verticalSlug, adminEmail, adminName, subscriptionTier, maxUsers])

  // Navigation guard
  const { blocker, skipGuard } = useUnsavedChanges(isDirty)

  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited) {
      const generated = name ? generateSlug(name) : ''
      setSlug(generated)
    }
  }, [name, slugManuallyEdited])

  // Debounce slug for uniqueness check
  useEffect(() => {
    if (slugDebounceRef.current) {
      clearTimeout(slugDebounceRef.current)
    }
    slugDebounceRef.current = setTimeout(() => {
      setDebouncedSlug(slug)
    }, 300)
    return () => {
      if (slugDebounceRef.current) {
        clearTimeout(slugDebounceRef.current)
      }
    }
  }, [slug])

  // Slug uniqueness check
  const {
    data: slugCheck,
    isLoading: slugCheckLoading,
    isFetching: slugCheckFetching,
  } = useQuery({
    queryKey: ['organizations', 'check-slug', debouncedSlug],
    queryFn: () => orgApi.checkSlug(debouncedSlug),
    enabled: debouncedSlug.length >= 2,
    retry: false,
  })

  // Fetch verticals
  const { data: verticals } = useQuery({
    queryKey: ['verticals'],
    queryFn: orgApi.listVerticals,
  })

  const activeVerticals = useMemo(
    () => verticals?.filter((v) => v.is_active) ?? [],
    [verticals]
  )

  // Auto-select first vertical when verticals load
  useEffect(() => {
    if (activeVerticals.length > 0 && !verticalSlug) {
      setVerticalSlug(activeVerticals[0].slug)
    }
  }, [activeVerticals, verticalSlug])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateOrgRequest) => orgApi.create(data),
    onSuccess: (result) => {
      toast({
        title: 'Organization created successfully',
        description: `${result.organization.name} has been created.`,
      })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      skipGuard()
      navigate(`/organizations/${result.organization.id}`)
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create organization',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Validation
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)
  const isSlugAvailable = slugCheck?.available === true
  const isSlugChecked = debouncedSlug.length >= 2 && !slugCheckLoading && !slugCheckFetching

  const canSubmit =
    name.length >= 2 &&
    slug.length >= 2 &&
    verticalSlug !== '' &&
    adminEmail !== '' &&
    isEmailValid &&
    adminName.length >= 1 &&
    maxUsers >= 1 &&
    maxUsers <= 10000 &&
    (isSlugChecked ? isSlugAvailable : true) &&
    !createMutation.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    createMutation.mutate({
      name,
      slug,
      vertical_slug: verticalSlug,
      admin_email: adminEmail,
      admin_name: adminName,
      subscription_tier: subscriptionTier,
      max_users: maxUsers,
    })
  }

  function handleSlugChange(value: string) {
    setSlugManuallyEdited(true)
    // Sanitize: lowercase, replace non-alphanum with hyphens
    const sanitized = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
    setSlug(sanitized)
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link
          to="/organizations"
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Organizations
        </Link>
        <span>/</span>
        <span className="text-foreground">Create Organization</span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Create Organization</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Set up a new organization with an admin user.
        </p>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Organization Name */}
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input
                id="org-name"
                placeholder="Acme Corporation"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
              />
              {name.length > 0 && name.length < 2 && (
                <p className="text-xs text-destructive">
                  Name must be at least 2 characters
                </p>
              )}
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="org-slug">Slug *</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="org-slug"
                  placeholder="acme-corporation"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  className="font-mono"
                />
                {slug.length >= 2 && (
                  <span className="flex items-center gap-1 text-sm whitespace-nowrap">
                    {slugCheckLoading || slugCheckFetching ? (
                      <CircleNotch className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : slugCheck?.available ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" weight="bold" />
                        <span className="text-green-600">Available</span>
                      </>
                    ) : slugCheck && !slugCheck.available ? (
                      <>
                        <X className="h-4 w-4 text-destructive" weight="bold" />
                        <span className="text-destructive">Already taken</span>
                      </>
                    ) : null}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                URL-safe identifier. Auto-generated from name, or edit manually.
              </p>
            </div>

            {/* Vertical */}
            <div className="space-y-2">
              <Label htmlFor="org-vertical">Vertical *</Label>
              <Select value={verticalSlug} onValueChange={setVerticalSlug}>
                <SelectTrigger id="org-vertical">
                  <SelectValue placeholder="Select a vertical" />
                </SelectTrigger>
                <SelectContent>
                  {activeVerticals.map((v) => (
                    <SelectItem key={v.slug} value={v.slug}>
                      {v.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Admin Email */}
            <div className="space-y-2">
              <Label htmlFor="admin-email">Admin Email *</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@example.com"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
              />
              {adminEmail.length > 0 && !isEmailValid && (
                <p className="text-xs text-destructive">
                  Enter a valid email address
                </p>
              )}
            </div>

            {/* Admin Name */}
            <div className="space-y-2">
              <Label htmlFor="admin-name">Admin Name *</Label>
              <Input
                id="admin-name"
                placeholder="John Doe"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
              />
            </div>

            {/* Subscription Tier & Max Users - side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="org-tier">Subscription Tier</Label>
                <Select value={subscriptionTier} onValueChange={setSubscriptionTier}>
                  <SelectTrigger id="org-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-max-users">Max Users</Label>
                <Input
                  id="org-max-users"
                  type="number"
                  min={1}
                  max={10000}
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(Number(e.target.value))}
                />
                {(maxUsers < 1 || maxUsers > 10000) && (
                  <p className="text-xs text-destructive">
                    Must be between 1 and 10,000
                  </p>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button type="submit" disabled={!canSubmit}>
                {createMutation.isPending && (
                  <CircleNotch className="h-4 w-4 animate-spin" />
                )}
                Create Organization
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/organizations')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Unsaved changes dialog */}
      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave this page?
              Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>
              Stay on page
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>
              Leave page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
